import React, { useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Badge,
  Box,
  Divider,
  Flex,
  Heading,
  Icon,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { MdPictureAsPdf } from 'react-icons/md';
import OrganizePdf from './components/OrganizePdf';
import MergePdf from './components/MergePdf';
import SplitPdf from './components/SplitPdf';
import RotatePdf from './components/RotatePdf';
import ConvertToPdfPage from './components/convert-to-pdf/ConvertToPdfPage';
import { STUDIO_TOOLS } from './constants';

// Configure pdfjs worker once at module load time
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

const PdfStudio = () => {
  const [activeToolId, setActiveToolId] = useState('organize');

  const subColor = useColorModeValue('gray.500', 'gray.400');
  const firstComingSoonIdx = STUDIO_TOOLS.findIndex((t) => t.comingSoon);

  return (
    <Box minH="calc(100vh - 64px)" display="flex" flexDirection="column" bg="gray.50">

      {/* ── Module header ── */}
      <Box px={{ base: 4, md: 6 }} pt={6} pb={5} bg="gray.50">
        <Heading as="h1" size="md" fontWeight="600" mb={0.5}>
          PDF Studio
        </Heading>
        <Text fontSize="sm" color={subColor}>
          Organize, merge, split, and edit your PDFs — entirely in your browser
        </Text>
      </Box>

      {/* ── Body: persistent sidebar + swappable content ── */}
      <Flex flex={1} minH={0}>

        {/* ── Persistent left sidebar ── */}
        <Box
          w="218px"
          minW="218px"
          bg="white"
          borderRightWidth="1px"
          borderColor="gray.100"
          p={5}
          display={{ base: 'none', lg: 'flex' }}
          flexDirection="column"
          flexShrink={0}
        >
          <Text
            fontSize="9px"
            fontWeight="700"
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="0.1em"
            mb={4}
          >
            PDF Tools
          </Text>

          <VStack spacing={1} align="stretch">
            {STUDIO_TOOLS.map((tool, i) => (
              <React.Fragment key={tool.id}>
                {i === firstComingSoonIdx && firstComingSoonIdx !== -1 && (
                  <Box py={2}>
                    <Divider borderColor="gray.100" />
                    <Text
                      fontSize="9px"
                      fontWeight="600"
                      color="gray.300"
                      mt={2}
                      mb={0.5}
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                    >
                      Coming Soon
                    </Text>
                  </Box>
                )}
                <Flex
                  align="center"
                  gap={2.5}
                  px={3}
                  py={2.5}
                  borderRadius="10px"
                  cursor={tool.active ? 'pointer' : 'not-allowed'}
                  bg={tool.id === activeToolId ? 'blue.50' : 'transparent'}
                  opacity={tool.active ? 1 : 0.45}
                  _hover={tool.active && tool.id !== activeToolId ? { bg: 'gray.50' } : {}}
                  transition="background 0.15s"
                  onClick={tool.active ? () => setActiveToolId(tool.id) : undefined}
                >
                  <Box color={tool.id === activeToolId ? 'blue.600' : tool.active ? 'gray.500' : 'gray.400'}>
                    <tool.Icon size={15} />
                  </Box>
                  <Text
                    as="span"
                    fontSize="sm"
                    fontWeight={tool.id === activeToolId ? '600' : '500'}
                    color={tool.id === activeToolId ? 'blue.700' : tool.active ? 'gray.600' : 'gray.500'}
                    flex={1}
                    lineHeight="1"
                  >
                    {tool.label}
                  </Text>
                  {tool.comingSoon && (
                    <Badge fontSize="8px" colorScheme="gray" variant="subtle" borderRadius="full" px={1.5}>
                      Soon
                    </Badge>
                  )}
                </Flex>
              </React.Fragment>
            ))}
          </VStack>

          <Box mt="auto" pt={6}>
            <Flex align="flex-start" gap={2} p={3} bg="gray.50" borderRadius="12px">
              <Icon as={MdPictureAsPdf} color="gray.300" boxSize={3.5} mt={0.5} flexShrink={0} />
              <Text fontSize="10px" color="gray.400" lineHeight="tall">
                All processing happens locally. Your files never leave this device.
              </Text>
            </Flex>
          </Box>
        </Box>

        {/* ── Content panels — both stay mounted to preserve state ── */}
        <Box
          display={activeToolId === 'organize' ? 'flex' : 'none'}
          flex={1}
          flexDirection="column"
          minW={0}
          overflow="hidden"
        >
          <OrganizePdf />
        </Box>

        <Box
          display={activeToolId === 'merge' ? 'flex' : 'none'}
          flex={1}
          flexDirection="column"
          minW={0}
          overflow="hidden"
        >
          <MergePdf />
        </Box>

        <Box
          display={activeToolId === 'split' ? 'flex' : 'none'}
          flex={1}
          flexDirection="column"
          minW={0}
          overflow="hidden"
        >
          <SplitPdf />
        </Box>

        <Box
          display={activeToolId === 'convert-to-pdf' ? 'flex' : 'none'}
          flex={1}
          flexDirection="column"
          minW={0}
          overflow="hidden"
        >
          <ConvertToPdfPage />
        </Box>

        <Box
          display={activeToolId === 'rotate' ? 'flex' : 'none'}
          flex={1}
          flexDirection="column"
          minW={0}
          overflow="hidden"
        >
          <RotatePdf />
        </Box>

      </Flex>
    </Box>
  );
};

export default PdfStudio;
